"use client";

import { useEffect } from "react";

// Top-level error boundary for the App Router. Next.js renders this in place of
// the root layout when a render throws, so it must supply its own <html> and
// <body> and cannot rely on the global CSS imported by the layout.
//
// The site ships many deploys a day. A tab opened before a deploy can request a
// JS chunk whose hash the new deployment no longer serves, which throws
// ChunkLoadError and dead-ends the page. We reload once to fetch the current
// asset manifest so a stale chunk self-heals. A time-boxed sessionStorage guard
// stops a genuinely broken chunk from looping the reload.

const RELOAD_GUARD_KEY = "chunk-reload-at";
const RELOAD_WINDOW_MS = 10_000;

function isChunkLoadError(error?: Error): boolean {
  if (!error) return false;
  const message = error.message ?? "";
  return (
    error.name === "ChunkLoadError" ||
    /Loading (CSS )?chunk [^ ]+ failed/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
}

export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string };
}) {
  useEffect(() => {
    if (!isChunkLoadError(error)) return;

    try {
      const last = Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY)) || 0;
      // Skip the reload if we already tried within the window, so a chunk that
      // stays missing shows the fallback instead of looping.
      if (Date.now() - last < RELOAD_WINDOW_MS) return;
      window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    } catch {
      // sessionStorage can be blocked (private mode, denied cookies). Without a
      // guard we cannot break a loop, so we leave the fallback in place.
      return;
    }

    window.location.reload();
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          fontFamily:
            'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
          color: "#111827",
          background: "#ffffff",
        }}
      >
        <main style={{ maxWidth: "420px", textAlign: "center" }}>
          <h1
            style={{
              fontSize: "1.5rem",
              fontWeight: 600,
              margin: "0 0 12px",
            }}
          >
            This page needs a refresh
          </h1>
          <p
            style={{
              fontSize: "1rem",
              lineHeight: 1.5,
              margin: "0 0 24px",
              color: "#4b5563",
            }}
          >
            We updated the site while this tab was open. Reload to load the
            latest version.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              height: "48px",
              padding: "0 28px",
              borderRadius: "40px",
              border: "none",
              background: "#000000",
              color: "#ffffff",
              fontSize: "1rem",
              cursor: "pointer",
            }}
          >
            Reload page
          </button>
        </main>
      </body>
    </html>
  );
}
